/**
 * i18n Configuration
 *
 * Configures react-i18next for SPA internationalization.
 * Translations are loaded from messages/*.json files.
 * Locale is determined from URL parameter (/:locale/).
 *
 * @module lib/i18n
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation resources
import { LOCALE_BUNDLES } from './locale-bundles';
export { LOCALE_BUNDLES } from './locale-bundles';

// ============================================================================
// Types & Constants
// ============================================================================

/** Supported locales */
export const SUPPORTED_LOCALES = ['en', 'ru'] as const;

/** Locale type */
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Default locale for fallback */
export const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * The exact locale objects compiled into i18next. Read-only consumers, such as
 * the locale bundle viewer, must use this export rather than loading a second
 * copy from disk or an API.
 */
const LOCALE_STORAGE_KEY = 'molchanovs.locale';

/** Locale display info */
export const LOCALE_INFO: Record<
  SupportedLocale,
  { name: string; nativeName: string; flag: string }
> = {
  en: { name: 'English', nativeName: 'English', flag: '🇬🇧' },
  ru: { name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
};

function getStoredLocale(): SupportedLocale | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return raw && isValidLocale(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

function setStoredLocale(locale: SupportedLocale): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore storage write errors (private mode, blocked storage, etc.)
  }
}

/**
 * Get the preferred locale (stored user choice or default).
 */
export function getPreferredLocale(): SupportedLocale {
  return getStoredLocale() ?? DEFAULT_LOCALE;
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Check if a string is a valid supported locale.
 */
export function isValidLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Get a valid locale, falling back to default if invalid.
 */
export function getValidLocale(locale: string | undefined): SupportedLocale {
  return locale && isValidLocale(locale) ? locale : DEFAULT_LOCALE;
}

// ============================================================================
// i18next Configuration
// ============================================================================

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: LOCALE_BUNDLES.en },
    ru: { translation: LOCALE_BUNDLES.ru },
  },
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES,

  interpolation: {
    escapeValue: false, // React already escapes by default
  },

  // Namespace configuration
  ns: ['translation'],
  defaultNS: 'translation',

  // Development options
  debug: import.meta.env.DEV && import.meta.env.MODE !== 'test',

  // Return empty string for missing keys in production
  returnEmptyString: false,
  returnNull: false,

  // Key separator for nested keys (e.g., "common.loading")
  keySeparator: '.',

  // React-specific options
  react: {
    useSuspense: false, // We handle loading states ourselves
  },
});

// ============================================================================
// Language Sync
// ============================================================================

/**
 * Sync i18next language with URL locale parameter.
 * Call this when the route locale changes.
 * Also updates the document lang attribute for accessibility.
 *
 * @param urlLocale - The locale from the URL parameter
 * @returns The validated locale that was set
 */
export function syncLanguageFromUrl(urlLocale: string): SupportedLocale {
  const validLocale = getValidLocale(urlLocale);

  if (i18n.language !== validLocale) {
    void i18n.changeLanguage(validLocale);
  }

  // Update document lang attribute for accessibility (WCAG 3.1.1)
  if (typeof document !== 'undefined') {
    document.documentElement.lang = validLocale;
  }

  // Persist preference for future visits and root redirects
  setStoredLocale(validLocale);

  return validLocale;
}

/**
 * Get the current i18next language as a valid locale.
 */
export function getCurrentLocale(): SupportedLocale {
  return getValidLocale(i18n.language);
}

/**
 * Change the i18next language.
 * This is typically called by the LocaleSwitcher component.
 *
 * @param locale - The new locale to set
 */
export function changeLanguage(locale: SupportedLocale): void {
  if (isValidLocale(locale) && i18n.language !== locale) {
    void i18n.changeLanguage(locale);
  }

  setStoredLocale(locale);
}

export default i18n;
